/**
 * Return Study Modal
 *
 * Modal for returning a worklist item with a reason
 * Includes voice dictation support using the existing speech recognition hook
 */

import { useState, useCallback } from 'react'
import { Loader2, Mic, MicOff, AlertCircle } from 'lucide-react'
import { useReturnWorklistItem } from '@/application/worklist'
import type { WorklistItem } from '@/domain/worklist'
import { useDictation, useDictationAvailable } from '@/application/dictation'
import { Button } from '@/presentation/components/ui/Button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/presentation/components/ui/Dialog'
import { toast } from '@/lib/toast'

// =============================================================================
// Types
// =============================================================================

interface ReturnStudyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: WorklistItem | null
  onSuccess?: () => void
}

// =============================================================================
// Component
// =============================================================================

export function ReturnStudyModal({
  open,
  onOpenChange,
  item,
  onSuccess,
}: ReturnStudyModalProps) {
  const [reason, setReason] = useState<string>('')

  const returnMutation = useReturnWorklistItem()
  const speechSupported = useDictationAvailable()

  // Handle speech recognition result
  const handleSpeechResult = useCallback((text: string) => {
    setReason((prev) => {
      const separator = prev && !prev.endsWith(' ') && !prev.endsWith('\n') ? ' ' : ''
      return prev + separator + text
    })
  }, [])

  const {
    isListening,
    startListening,
    stopListening,
  } = useDictation({
    lang: 'es-CO',
    processCommands: false,
    onResult: handleSpeechResult,
    // Este modal ya avisa por toast del resto de fallos; el del dictado
    // tambien. Un boton que no dice por que no funciona es el defecto que se
    // vino a corregir.
    onError: (mensaje) => toast.error(mensaje),
  })

  const toggleRecording = () => {
    if (isListening) {
      stopListening()
    } else {
      startListening()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!item) return

    const trimmedReason = reason.trim()
    if (!trimmedReason) {
      toast.error('Debe proporcionar un motivo de devolucion')
      return
    }

    try {
      await returnMutation.mutateAsync({
        id: item.id,
        reason: trimmedReason,
      })

      toast.success('Estudio devuelto exitosamente')
      setReason('')
      stopListening()
      onOpenChange(false)
      onSuccess?.()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al devolver el estudio')
    }
  }

  const handleClose = () => {
    setReason('')
    stopListening()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogClose onClose={handleClose} />
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-500" />
            Devolver Estudio
          </DialogTitle>
          <DialogDescription>
            Proporcione el motivo por el cual esta devolviendo este estudio. Puede escribir o usar el dictado de voz.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Reason Input with Voice Dictation */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-[var(--foreground)]">
                Motivo de devolucion <span className="text-red-500">*</span>
              </label>
              {speechSupported && (
                <Button
                  type="button"
                  variant={isListening ? 'destructive' : 'outline'}
                  size="sm"
                  onClick={toggleRecording}
                  className="flex items-center gap-1"
                >
                  {isListening ? (
                    <>
                      <MicOff className="h-4 w-4" />
                      <span className="text-xs">Detener</span>
                    </>
                  ) : (
                    <>
                      <Mic className="h-4 w-4" />
                      <span className="text-xs">Dictar</span>
                    </>
                  )}
                </Button>
              )}
            </div>

            <div className="relative">
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Describa el motivo de la devolucion..."
                className={
                  'w-full rounded-md border bg-[var(--background)] px-3 py-2 text-sm resize-none transition-colors ' +
                  (isListening
                    ? 'border-red-500 ring-2 ring-red-200'
                    : 'border-[var(--border)]')
                }
                rows={5}
              />
              {isListening && (
                <div className="absolute bottom-2 right-2 flex items-center gap-1">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                  <span className="text-xs text-red-500">Grabando...</span>
                </div>
              )}
            </div>

            {!speechSupported && (
              <p className="text-xs text-[var(--foreground-muted)] mt-1">
                El dictado de voz no esta disponible en este navegador
              </p>
            )}
          </div>

          {/* Common Reasons (Quick Select) */}
          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
              Motivos comunes
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                'Calidad de imagen insuficiente',
                'Estudio incompleto',
                'Informacion del paciente incorrecta',
                'Imagen borrosa o movida',
                'Falta historia clinica',
              ].map((quickReason) => (
                <button
                  key={quickReason}
                  type="button"
                  onClick={() => setReason((prev) =>
                    prev ? `${prev} ${quickReason}.` : quickReason
                  )}
                  className="px-2 py-1 text-xs rounded-full border border-[var(--border)] hover:bg-[var(--background-subtle)] transition-colors"
                >
                  {quickReason}
                </button>
              ))}
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={!reason.trim() || returnMutation.isPending}
            >
              {returnMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Devolviendo...
                </>
              ) : (
                'Devolver Estudio'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
